import { useMemo } from 'react';

import type { MeQuery } from '@graphql/__generated__/graphql';
import { makeLikePrefixCandidates } from '@modules/_shared/utils/agencyNamePatterns';

type TeamsUser = NonNullable<MeQuery['get_me']['me']>['teams'][number];
type Team = TeamsUser['team'];

type AgencyNameFilter = { agency_name: { _ilike?: string; _in?: string[] } };

export type AgencyFilter = {
  _or: AgencyNameFilter[];
};

const buildTeamAgencyFilter = (team: Team): AgencyFilter | null => {
  const filters: AgencyNameFilter[] = [];

  if (team.listings_agency_names && team.listings_agency_names.length > 0) {
    filters.push({ agency_name: { _in: team.listings_agency_names } });
  } else {
    const namePatterns = makeLikePrefixCandidates(team.name);
    for (const pattern of namePatterns) {
      filters.push({ agency_name: { _ilike: pattern } });
    }
  }

  return filters.length > 0 ? { _or: filters } : null;
};

export const useAgencyFilter = (
  teamsUsers: TeamsUser[] | null | undefined,
): AgencyFilter | null => {
  return useMemo(() => {
    const teams = teamsUsers?.map(t => t.team).filter(team => team != null);

    if (!teams?.length) {
      return null;
    }

    const allFilters: AgencyNameFilter[] = [];

    for (const team of teams) {
      const teamFilter = buildTeamAgencyFilter(team);
      if (teamFilter?._or) {
        allFilters.push(...teamFilter._or);
      }
    }

    return allFilters.length > 0 ? { _or: allFilters } : null;
  }, [teamsUsers]);
};
